<template>
  <iColumn
    v-if="editMode"
    data-test="app-component-placeholder"
    class="app-component-placeholder"
    width="fill"
    height="hug"
    align="center"
    vertical-align="middle"
  >
    <iText variant="subtle">
      {{ placeholderLabel }}
    </iText>
  </iColumn>
  <AppComponent
    v-else
    :component-name="componentName"
    :app-name="contextAppName"
    :contexts="mergedContexts"
  />
</template>

<script>
// Standalone embed of an app-shipped component. Edit mode must render a
// placeholder — the editor's contexts are pk-less, so a live render is impossible.
import AppComponent from "@bloglovin/inspire-engine/Components/AppComponent.vue";
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import { Context } from "%/dynamic-component-editor/classes/context-v2";
import ContextBindings from "%/dynamic-component-editor/options/context-bindings";

export default {
  name: "DynamicAppComponentElement",
  components: { AppComponent },
  // NOT DynamicElementWithContext: its created() throws when the binding
  // lacks entityContext, and this binding never has one.
  mixins: [DynamicElementMixin],
  inject: {
    scopedProvidedContexts: {
      from: "scopedProvidedContexts",
      default: null,
    },
  },
  elementPanelSettings: {
    displayName: "App Component",
    elementGroup: "reusable",
    displayIcon: "box",
    elementType: "element",
  },
  options: [ContextBindings],
  hidden: true,
  computed: {
    elementContext() {
      const elementConfig = this.dynamicComponentInstance?.getElement(this.identifier);
      return this.optionValues?.context
        || elementConfig?.options?.context
        || elementConfig?.context
        || this.configuration?.options?.context
        || this.configuration?.context
        || {};
    },
    contextAppName() {
      return this.elementContext.appName;
    },
    componentName() {
      return this.elementContext.componentName;
    },
    placeholderLabel() {
      if (!this.componentName) {
        return "App Component";
      }
      return `${this.componentName} (${this.contextAppName})`;
    },
    mergedContexts() {
      const scopedContexts = Object.fromEntries(
        Object.entries(this.scopedProvidedContexts?.() ?? {}).map(([contextName, scopedItem]) => [
          contextName,
          {
            entityType: scopedItem.entityType,
            pk: scopedItem.entityPkUrlVars,
            ...scopedItem.appName ? { appName: scopedItem.appName } : {},
          },
        ]),
      );
      return {
        ...Context.contexts,
        ...scopedContexts,
      };
    },
  },
  created() {
    if (!this.contextAppName || !this.componentName) {
      // eslint-disable-next-line no-console
      console.error(
        "DynamicAppComponentElement: missing { appName, componentName } context binding — "
        + "the element renders empty",
        { identifier: this.identifier, elementContext: this.elementContext },
      );
    }
  },
};
</script>

<style scoped>
.app-component-placeholder {
  border: 1px dashed #b0b0b0;
  border-radius: 4px;
  padding: 12px;
}
</style>
